import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { FinanceOverview } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from './money';

type Planning = {
  today: string;
  goals: { id: string; name: string; targetCents: number; currentCents: number; accountId: string | null; categoryId: string | null; period: string; deadline: string | null }[];
  obligations: { id: string; name: string; amountCents: number; frequency: string; nextDue: string; accountId: string | null; categoryId: string | null; notes: string | null; overdue: boolean }[];
};
const PERIOD: Record<string, string> = { semanal: 'esta semana', mensual: 'este mes', anual: 'este año', hasta_fecha: 'acumulado' };
const FREQ: Record<string, string> = { semanal: 'Semanal', mensual: 'Mensual', bimestral: 'Bimestral', trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual' };
const money = (c: number) => formatMoney(c, 'MXN');
const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });

/** Metas (p. ej. Construcción) y obligaciones (pagos fijos) de Finanzas. */
export function PlanningCards({ churchId, overview, canManage, onChange }: { churchId: string; overview: FinanceOverview; canManage: boolean; onChange: (m?: string) => void }) {
  const base = `/finance/churches/${churchId}`;
  const data = useAsync((signal) => api.get<Planning>(`${base}/planning`, signal), [base, overview]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState<'goal' | 'obligation' | null>(null);
  const [paying, setPaying] = useState<string | null>(null);
  if (data.status !== 'ready') return null;
  const p = data.data;
  const accounts = overview.accounts.filter((a) => !a.archived);
  const income = overview.categories.filter((c) => c.kind === 'income');
  const expense = overview.categories.filter((c) => c.kind === 'expense');
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      data.reload();
      onChange(ok);
      return true;
    } catch (e) {
      setMsg(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card title="Metas" action={canManage && adding !== 'goal' ? <Button variant="ghost" onClick={() => setAdding('goal')}>Nueva meta</Button> : null}>
        {msg ? <Alert>{msg}</Alert> : null}
        {p.goals.length === 0 && adding !== 'goal' ? <p className="muted small">Sin metas. Ejemplo: «Construcción: $500,000».</p> : null}
        {p.goals.map((g) => (
          <div key={g.id} className="goal">
            <p><strong>{g.name}</strong> <span className="small muted">· {money(g.currentCents)} de {money(g.targetCents)} {PERIOD[g.period] ?? ''}{g.deadline ? ` · para el ${day(g.deadline)}` : ''}</span></p>
            <progress className="usage" max={g.targetCents} value={Math.max(0, Math.min(g.currentCents, g.targetCents))} aria-label={`Avance de ${g.name}`} />
            {canManage ? <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/goals/${g.id}`), 'Meta archivada.')}>Archivar</Button> : null}
          </div>
        ))}
        {adding === 'goal' ? (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const [kind, id] = String(f.get('measure') ?? '').split(':');
              void run(() => api.post(`${base}/goals`, {
                name: String(f.get('name') ?? '').trim(), targetCents: parseMoney(String(f.get('target') ?? '')), accountId: kind === 'a' ? id : null, categoryId: kind === 'c' ? id : null,
                period: String(f.get('period') ?? 'hasta_fecha'), deadline: String(f.get('deadline') ?? '') || null,
              }), 'Meta creada.').then((ok) => ok && setAdding(null));
            }}
          >
            <Field label="Nombre" name="name" required maxLength={120} placeholder="Construcción del templo" />
            <Field label="Meta" name="target" inputMode="decimal" required placeholder="500000" />
            <div className="field">
              <label htmlFor="goal-measure">Se mide con</label>
              <select id="goal-measure" name="measure">
                {accounts.map((a) => <option key={a.id} value={`a:${a.id}`}>Saldo de la cuenta «{a.name}»</option>)}
                {income.map((c) => <option key={c.id} value={`c:${c.id}`}>Lo que entra a «{c.name}»</option>)}
              </select>
            </div>
            <div className="form__row">
              <div className="field">
                <label htmlFor="goal-period">Periodo (si es por categoría)</label>
                <select id="goal-period" name="period" defaultValue="mensual">
                  <option value="semanal">Cada semana</option><option value="mensual">Cada mes</option><option value="anual">Cada año</option><option value="hasta_fecha">Hasta una fecha</option>
                </select>
              </div>
              <Field label="Fecha límite (opcional)" name="deadline" type="date" />
            </div>
            <div className="actions"><Button type="submit" busy={busy}>Crear meta</Button><Button variant="ghost" onClick={() => setAdding(null)}>Cancelar</Button></div>
          </form>
        ) : null}
      </Card>

      <Card title="Pagos fijos (obligaciones)" action={canManage && adding !== 'obligation' ? <Button variant="ghost" onClick={() => setAdding('obligation')}>Nuevo pago fijo</Button> : null}>
        {p.obligations.length === 0 && adding !== 'obligation' ? <p className="muted small">Renta, luz, internet… KEORA avisa 3 días antes.</p> : null}
        <ul className="list">
          {p.obligations.map((o) => (
            <li key={o.id} className="list__item list__item--stack">
              <span>
                <strong>{o.name}</strong> · {money(o.amountCents)} · {FREQ[o.frequency]} · <span className={o.overdue ? 'text-danger' : ''}>{o.overdue ? 'vencido desde' : 'vence'} el {day(o.nextDue)}</span>
              </span>
              {canManage ? (
                paying === o.id ? (
                  <form
                    className="family-add"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      void run(() => api.post(`${base}/obligations/${o.id}/pay`, { accountId: String(f.get('account')), categoryId: String(f.get('category')), amountCents: parseMoney(String(f.get('amount') ?? '')) }), `${o.name} pagado.`).then((ok) => ok && setPaying(null));
                    }}
                  >
                    <div className="field">
                      <label htmlFor={`ob-acc-${o.id}`}>De la cuenta</label>
                      <select id={`ob-acc-${o.id}`} name="account" defaultValue={o.accountId ?? accounts[0]?.id}>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
                    </div>
                    <div className="field">
                      <label htmlFor={`ob-cat-${o.id}`}>Categoría</label>
                      <select id={`ob-cat-${o.id}`} name="category" defaultValue={o.categoryId ?? expense[0]?.id}>{expense.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
                    </div>
                    <Field label="Monto" name="amount" inputMode="decimal" defaultValue={String(o.amountCents / 100)} />
                    <Button type="submit" busy={busy}>Registrar pago</Button>
                  </form>
                ) : (
                  <div className="actions">
                    <Button variant="secondary" onClick={() => setPaying(o.id)}>Marcar pagado</Button>
                    <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/obligations/${o.id}`), 'Pago fijo quitado.')}>Quitar</Button>
                  </div>
                )
              ) : null}
            </li>
          ))}
        </ul>
        {adding === 'obligation' ? (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void run(() => api.post(`${base}/obligations`, {
                name: String(f.get('name') ?? '').trim(), amountCents: parseMoney(String(f.get('amount') ?? '')), frequency: String(f.get('frequency')), nextDue: String(f.get('nextDue')),
                accountId: null, categoryId: String(f.get('category') ?? '') || null, notes: null,
              }), 'Pago fijo agregado.').then((ok) => ok && setAdding(null));
            }}
          >
            <Field label="Concepto" name="name" required maxLength={120} placeholder="Renta del local" />
            <div className="form__row form__row--3">
              <Field label="Monto" name="amount" inputMode="decimal" required />
              <div className="field">
                <label htmlFor="ob-freq">Cada cuándo</label>
                <select id="ob-freq" name="frequency" defaultValue="mensual">{Object.entries(FREQ).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              </div>
              <Field label="Próximo pago" name="nextDue" type="date" required defaultValue={p.today} />
            </div>
            <div className="field">
              <label htmlFor="ob-cat">Categoría</label>
              <select id="ob-cat" name="category">{expense.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            </div>
            <div className="actions"><Button type="submit" busy={busy}>Agregar</Button><Button variant="ghost" onClick={() => setAdding(null)}>Cancelar</Button></div>
          </form>
        ) : null}
      </Card>
    </>
  );
}
